import { CheckOutlined, PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Card,
  Descriptions,
  Flex,
  Form,
  Input,
  Skeleton,
  Space,
  Steps,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ActionDrawer } from '../../components/ActionDrawer';
import { DetailPage } from '../../components/DetailPage';
import { ApiError } from '../../lib/api-client';
import { formatDate, orDash } from '../../lib/format';
import {
  getRegistrationCase,
  updateChecklistItem,
  updateStage,
  type RegistrationStage,
  type StageChecklistItem,
} from './api';
import {
  STAGE_DESCRIPTION,
  STAGE_LABEL,
  STAGE_ORDER,
  STAGE_STATUS_LABEL,
  overdueTone,
  stageTone,
} from './registration-status';

const { Text } = Typography;

export function RegistrationCasePage(): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { uid = '' } = useParams<{ uid: string }>();

  const [finishing, setFinishing] = useState<RegistrationStage | null>(null);
  const [blocking, setBlocking] = useState<RegistrationStage | null>(null);
  const [waiving, setWaiving] = useState<StageChecklistItem | null>(null);
  const [referenceNo, setReferenceNo] = useState('');
  const [reason, setReason] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['registration-case', uid],
    queryFn: () => getRegistrationCase(uid),
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['registration-case', uid] });
    void queryClient.invalidateQueries({ queryKey: ['registration-cases'] });
    void queryClient.invalidateQueries({ queryKey: ['units'] });
  };
  const fail = (e: unknown, fallback: string): void => {
    void message.error(e instanceof ApiError ? e.message : fallback);
  };

  const move = useMutation({
    mutationFn: (input: {
      stageUid: string;
      status?: RegistrationStage['status'];
      referenceNo?: string;
      reason?: string;
    }) => {
      const { stageUid, ...rest } = input;
      return updateStage(stageUid, rest);
    },
    onSuccess: () => {
      refresh();
      setFinishing(null);
      setBlocking(null);
    },
    // The API's refusals name the missing documents and the gate that is not
    // done yet. Showing them verbatim is why it bothers.
    onError: (e: unknown) => {
      fail(e, t('registration.stageFailed'));
    },
  });

  const waive = useMutation({
    mutationFn: (input: { itemUid: string; note: string }) =>
      updateChecklistItem(input.itemUid, { status: 'WAIVED', note: input.note }),
    onSuccess: () => {
      refresh();
      setWaiving(null);
    },
    onError: (e: unknown) => {
      fail(e, t('registration.documentFailed'));
    },
  });

  if (isLoading || data === undefined) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    );
  }

  const currentIndex =
    data.currentStage === null ? STAGE_ORDER.length : STAGE_ORDER.indexOf(data.currentStage);

  const stageCard = (stage: RegistrationStage): ReactNode => {
    const isCurrent = stage.stage === data.currentStage;
    const checklist = stage.checklist;
    const canStart = stage.status === 'PENDING' || stage.status === 'BLOCKED';
    const canFinish = stage.status === 'IN_PROGRESS';

    return (
      <Card
        key={stage.uid}
        title={
          <Flex align="center" gap={8} wrap>
            <Text strong>{STAGE_LABEL[stage.stage]}</Text>
            <Tag color={stageTone(stage.status)}>{STAGE_STATUS_LABEL[stage.status]}</Tag>
            {stage.daysOverdue !== null && (
              <Text type={overdueTone(stage.daysOverdue)} strong>
                {t('registration.daysLate', { count: stage.daysOverdue })}
              </Text>
            )}
          </Flex>
        }
        extra={
          isCurrent ? (
            <Space wrap>
              {canStart && (
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  loading={move.isPending}
                  onClick={() => {
                    move.mutate({ stageUid: stage.uid, status: 'IN_PROGRESS' });
                  }}
                >
                  {t('registration.start')}
                </Button>
              )}
              {canFinish && (
                <>
                  <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    disabled={checklist !== null && !checklist.isComplete}
                    onClick={() => {
                      setReferenceNo(stage.referenceNo ?? '');
                      setFinishing(stage);
                    }}
                  >
                    {t('registration.finish')}
                  </Button>
                  <Button
                    danger
                    icon={<PauseCircleOutlined />}
                    onClick={() => {
                      setReason('');
                      setBlocking(stage);
                    }}
                  >
                    {t('registration.block')}
                  </Button>
                </>
              )}
            </Space>
          ) : null
        }
      >
        <Flex vertical gap={12}>
          <Text type="secondary">{STAGE_DESCRIPTION[stage.stage]}</Text>

          {stage.status === 'BLOCKED' && (
            <Alert
              type="error"
              showIcon
              message={t('registration.blockedOn')}
              description={orDash(stage.blockedReason)}
            />
          )}

          <Descriptions column={{ xs: 1, sm: 2 }} bordered size="small">
            <Descriptions.Item label={t('registration.fields.reference')}>
              {orDash(stage.referenceNo)}
            </Descriptions.Item>
            <Descriptions.Item label={t('registration.fields.owner')}>
              {stage.ownerName === null ? (
                <Text type="warning">{t('registration.unowned')}</Text>
              ) : (
                stage.ownerName
              )}
            </Descriptions.Item>
            <Descriptions.Item label={t('registration.fields.started')}>
              {formatDate(stage.startedAt)}
            </Descriptions.Item>
            <Descriptions.Item label={t('registration.fields.target')}>
              {stage.targetDate === null ? (
                <Text type="secondary">{t('registration.targetOnStart', { days: stage.targetDays })}</Text>
              ) : (
                <Text type={overdueTone(stage.daysOverdue)}>{formatDate(stage.targetDate)}</Text>
              )}
            </Descriptions.Item>
          </Descriptions>

          {checklist !== null && (
            <Table<StageChecklistItem>
              dataSource={checklist.items}
              rowKey={(row) => row.uid}
              pagination={false}
              size="small"
              scroll={{ x: 'max-content' }}
              columns={[
                {
                  title: t('registration.fields.document'),
                  dataIndex: 'label',
                  render: (label: string, row: StageChecklistItem) => (
                    <Flex vertical gap={2}>
                      <Text>{label}</Text>
                      {row.note !== null && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {row.note}
                        </Text>
                      )}
                    </Flex>
                  ),
                },
                {
                  title: t('registration.fields.required'),
                  dataIndex: 'isMandatory',
                  width: 110,
                  render: (mandatory: boolean) =>
                    mandatory ? (
                      <Text>{t('registration.required')}</Text>
                    ) : (
                      <Text type="secondary">{t('registration.optional')}</Text>
                    ),
                },
                {
                  title: t('sales.fields.status'),
                  dataIndex: 'status',
                  width: 140,
                  render: (status: StageChecklistItem['status']) =>
                    status === 'PENDING' ? (
                      <Text type="secondary">{t('registration.notYet')}</Text>
                    ) : (
                      <Tag color="success">{t(`registration.item.${status}`)}</Tag>
                    ),
                },
                {
                  title: '',
                  width: 90,
                  render: (_: unknown, row: StageChecklistItem) =>
                    isCurrent && stage.status !== 'DONE' && row.status === 'PENDING' ? (
                      <Button
                        size="small"
                        onClick={() => {
                          setReason('');
                          setWaiving(row);
                        }}
                      >
                        {t('registration.waive')}
                      </Button>
                    ) : null,
                },
              ]}
            />
          )}
        </Flex>
      </Card>
    );
  };

  return (
    <>
      <DetailPage
        title={orDash(data.customerName)}
        subtitle={`${data.caseNo} · ${data.chassisNo}`}
        tags={[
          {
            label: data.status === 'COMPLETE' ? t('registration.complete') : t('registration.open'),
            colour: data.status === 'COMPLETE' ? 'success' : undefined,
          },
          ...(data.worstOverdueDays !== null
            ? [
                {
                  label: t('registration.daysLate', { count: data.worstOverdueDays }),
                  colour: 'error',
                },
              ]
            : []),
        ]}
        onBack={() => {
          void navigate('/registration');
        }}
        tabs={[
          {
            key: 'gates',
            label: t('registration.tabs.gates'),
            content: (
              <Flex vertical gap={16}>
                {data.status === 'COMPLETE' && (
                  <Alert
                    type="success"
                    showIcon
                    message={t('registration.completeTitle')}
                    description={t('registration.completeHelp')}
                  />
                )}

                <Card>
                  <Steps
                    current={currentIndex}
                    responsive
                    items={STAGE_ORDER.map((code) => {
                      const stage = data.stages.find((s) => s.stage === code);
                      return {
                        title: STAGE_LABEL[code],
                        description: stage === undefined ? '' : STAGE_STATUS_LABEL[stage.status],
                        status:
                          stage?.status === 'DONE'
                            ? ('finish' as const)
                            : stage?.status === 'BLOCKED'
                              ? ('error' as const)
                              : stage?.status === 'IN_PROGRESS'
                                ? ('process' as const)
                                : ('wait' as const),
                      };
                    })}
                  />
                </Card>

                {data.stages.map(stageCard)}
              </Flex>
            ),
          },
          {
            key: 'overview',
            label: t('sales.tabs.overview'),
            content: (
              <Card>
                <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
                  <Descriptions.Item label={t('vehicles.fields.chassisNo')}>
                    <Text strong>{data.chassisNo}</Text>
                  </Descriptions.Item>
                  <Descriptions.Item label={t('sales.fields.model')}>{orDash(data.model)}</Descriptions.Item>
                  <Descriptions.Item label={t('vehicles.fields.plateNo')}>
                    {orDash(data.plateNo)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('sales.fields.customer')}>
                    {orDash(data.customerName)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('bookings.fields.bookingNo')}>
                    {orDash(data.bookingNo)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('registration.fields.owner')}>
                    {orDash(data.ownerName)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('registration.fields.opened')}>
                    {formatDate(data.openedAt)}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('registration.fields.closed')}>
                    {formatDate(data.closedAt)}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            ),
          },
        ]}
      />

      <ActionDrawer
        open={finishing !== null}
        title={t('registration.finishTitle', {
          gate: finishing === null ? '' : STAGE_LABEL[finishing.stage],
        })}
        submitLabel={t('registration.finishSubmit')}
        submitting={move.isPending}
        onClose={() => {
          setFinishing(null);
        }}
        onSubmit={() => {
          if (finishing === null) return;
          move.mutate({
            stageUid: finishing.uid,
            status: 'DONE',
            ...(referenceNo.trim().length > 0 ? { referenceNo: referenceNo.trim() } : {}),
          });
        }}
        consequence={
          finishing?.stage === 'JPJ'
            ? t('registration.finishJpjConsequence')
            : t('registration.finishConsequence', {
                next:
                  finishing === null
                    ? ''
                    : STAGE_LABEL[STAGE_ORDER[STAGE_ORDER.indexOf(finishing.stage) + 1] ?? 'JPJ'],
              })
        }
      >
        <Form layout="vertical">
          <Form.Item label={t('registration.fields.reference')} help={t('registration.referenceHelp')}>
            <Input
              size="large"
              value={referenceNo}
              onChange={(e) => {
                setReferenceNo(e.target.value);
              }}
              placeholder="AP/2026/004821"
            />
          </Form.Item>
        </Form>
      </ActionDrawer>

      <ActionDrawer
        open={blocking !== null}
        danger
        title={t('registration.blockTitle', {
          gate: blocking === null ? '' : STAGE_LABEL[blocking.stage],
        })}
        submitLabel={t('registration.blockSubmit')}
        submitting={move.isPending}
        submitDisabled={reason.trim().length < 3}
        onClose={() => {
          setBlocking(null);
        }}
        onSubmit={() => {
          if (blocking === null) return;
          move.mutate({ stageUid: blocking.uid, status: 'BLOCKED', reason: reason.trim() });
        }}
        // It keeps ageing. Say so, or "blocked" becomes somewhere to park things.
        consequence={t('registration.blockConsequence')}
      >
        <Form layout="vertical">
          <Form.Item label={t('registration.fields.blockedOn')} required>
            <Input.TextArea
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              placeholder={t('registration.blockPlaceholder')}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>

      <ActionDrawer
        open={waiving !== null}
        title={waiving?.label ?? ''}
        submitLabel={t('registration.waiveSubmit')}
        submitting={waive.isPending}
        submitDisabled={reason.trim().length < 3}
        onClose={() => {
          setWaiving(null);
        }}
        onSubmit={() => {
          if (waiving === null) return;
          waive.mutate({ itemUid: waiving.uid, note: reason.trim() });
        }}
      >
        <Form layout="vertical">
          <Form.Item label={t('registration.fields.whyWaived')} required>
            <Input.TextArea
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              placeholder={t('registration.waivePlaceholder')}
            />
          </Form.Item>
          <Text type="secondary">{t('registration.waiveHelp')}</Text>
        </Form>
      </ActionDrawer>
    </>
  );
}
